/**
 * Composant conditionnel qui affiche soit :
 * - Section de livraison (tracking) si c'est une commande avec livraison
 * - Section QR codes pickup/return si c'est une commande avec retrait
 * 
 * Créé le 21 juillet 2026
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Divider
} from '@mui/material';
import {
  LocalShipping as LocalShippingIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { isDeliveryOrder, canConfirmShipment } from '../../utils/deliveryUtils';
import DeliveryTrackingForm from './DeliveryTrackingForm';
import DeliveryTrackingDisplay from './DeliveryTrackingDisplay';
import DeliveryInfoDisplay from './DeliveryInfoDisplay';
import ProductVariantDisplay from './ProductVariantDisplay';

const DeliveryOrPickupSection = ({
  reservation,
  currentUser,
  userRole,
  onReservationUpdate,
  scrollToTracking = false, // Nouvelle prop pour le scroll automatique
  onStartLoading, // Nouvelle prop pour activer l'overlay de chargement
  onError, // Nouvelle prop pour gérer les erreurs
  // Props pour la section QR codes (si retrait)
  renderPickupReturnSection
}) => {
  const { t } = useTranslation('delivery');
  const [showShipmentForm, setShowShipmentForm] = useState(false);
  const trackingSectionRef = useRef(null); // Ref pour la section de tracking

  // Scroll automatique vers la section de tracking si demandé
  useEffect(() => {
    if (scrollToTracking && trackingSectionRef.current) {
      console.log('🎯 Tentative de scroll vers la section tracking...');
      // Attendre que le modal soit complètement rendu
      const timer = setTimeout(() => {
        const element = trackingSectionRef.current;
        if (element) {
          console.log('📍 Élément trouvé, recherche du conteneur scrollable...');
          // Chercher le conteneur parent avec scroll
          let container = element.parentElement;
          while (container) {
            const style = window.getComputedStyle(container);
            if (style.overflow === 'auto' || style.overflowY === 'auto' || style.overflow === 'scroll' || style.overflowY === 'scroll') {
              // Trouvé le conteneur scrollable
              console.log('✅ Conteneur scrollable trouvé:', container);
              const containerRect = container.getBoundingClientRect();
              const elementRect = element.getBoundingClientRect();
              const scrollTop = elementRect.top - containerRect.top + container.scrollTop - 20;
              
              console.log('📊 Scroll vers position:', scrollTop);
              container.scrollTo({
                top: scrollTop,
                behavior: 'smooth'
              });
              console.log('✅ Scroll effectué vers la section tracking');
              break;
            }
            container = container.parentElement;
          }
          
          if (!container) {
            console.warn('⚠️ Aucun conteneur scrollable trouvé');
          }
        }
      }, 800); // Délai augmenté à 800ms

      return () => clearTimeout(timer);
    }
  }, [scrollToTracking]);

  // Détecter si c'est une commande avec livraison
  const isDelivery = isDeliveryOrder(reservation);

  // Si ce n'est PAS une livraison, afficher la section QR codes classique
  if (!isDelivery) {
    return renderPickupReturnSection();
  }

  // ========== MODE LIVRAISON ==========

  const canConfirm = canConfirmShipment(reservation, currentUser?.id);
  const isShipped = reservation.pickup_confirmed;

  const handleShipmentSuccess = (updatedReservation) => {
    setShowShipmentForm(false);
    if (onReservationUpdate) {
      onReservationUpdate(updatedReservation);
    }
  };
  
  const handleShipmentError = (errorMessage) => {
    // Propager l'erreur au parent si le callback existe
    if (onError) {
      onError(errorMessage);
    }
  };

  return (
    <Card elevation={1} sx={{ p: 3 }}>
      <Typography 
        variant="h6" 
        gutterBottom 
        sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
      >
        <LocalShippingIcon color="primary" />
        {t('sectionTitle', 'Livraison')}
      </Typography>

      {/* Affichage des variantes de produit si disponibles */}
      <ProductVariantDisplay 
        variantSelections={reservation.variant_selections}
        currency={reservation.currency}
      />

      {/* Affichage des informations de livraison (adresse, type, prix) */}
      <DeliveryInfoDisplay reservation={reservation} />

      <Divider sx={{ my: 3 }} />

      {/* Section de tracking avec ref et ID pour le scroll automatique */}
      <Box 
        ref={trackingSectionRef}
        id="delivery-tracking-section"
      >
        {/* Affichage des infos de tracking si déjà expédié */}
        {isShipped ? (
          <DeliveryTrackingDisplay 
            reservationId={reservation.id}
            reservation={reservation}
            isOwner={userRole === 'owner'}
          />
        ) : (
          <Box>
            {/* Si owner et peut confirmer, afficher le bouton */}
            {canConfirm && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {t('awaitingShipment', 'En attente d\'expédition')}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<LocalShippingIcon />}
                  onClick={() => setShowShipmentForm(true)}
                  color="primary"
                >
                  {t('confirmShipmentButton', 'Confirmer l\'expédition')}
                </Button>
              </Box>
            )}

            {/* Si renter, afficher message d'attente */}
            {userRole === 'renter' && !isShipped && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  ⏳ {t('awaitingShipmentFromSeller', 'En attente d\'expédition par le vendeur')}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Modal formulaire de confirmation d'expédition */}
      <Dialog
        open={showShipmentForm}
        onClose={() => setShowShipmentForm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {t('confirmShipment', 'Confirmer l\'expédition')}
          <IconButton onClick={() => setShowShipmentForm(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DeliveryTrackingForm
            reservationId={reservation.id}
            onSuccess={handleShipmentSuccess}
            onCancel={() => setShowShipmentForm(false)}
            onStartLoading={onStartLoading}
            onError={handleShipmentError}
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default DeliveryOrPickupSection;
